/**
 * Diagnosis Manager Component
 * 
 * Component for managing diagnoses nomenclature
 * Part of Diagnosis Nomenclature Implementation
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDiagnoses, useDiagnosisMutations, useDiagnosisSearch } from '../../hooks/useDiagnoses';
import { useNotifications } from '../NotificationSystem';
import Modal from '../ui/Modal';
import { Search, Plus, Edit2, Trash2, Save, X, Link2, ExternalLink } from 'lucide-react';
import initializeCommonHAIDiagnoses from '../../scripts/initialize-common-hai-diagnoses';
import './DiagnosisManager.css';

const DiagnosisManager = ({ organizationId, userId, userName }) => {
  const { t } = useTranslation('ipc');
  const { diagnoses, loading, error, refresh } = useDiagnoses(organizationId, { activeOnly: false });
  const { createDiagnosis, updateDiagnosis, deleteDiagnosis, loading: mutating } = useDiagnosisMutations();
  const { searchResults, searchDiagnoses, loading: searching } = useDiagnosisSearch(organizationId);
  const { notify } = useNotifications();

  const [showForm, setShowForm] = useState(false);
  const [editingDiagnosis, setEditingDiagnosis] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [diagnosisToDelete, setDiagnosisToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showInitializeModal, setShowInitializeModal] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [initResult, setInitResult] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    haiType: '',
    ipcRelevant: false,
    isHAI: false
  });

  const [formData, setFormData] = useState({
    icd10Code: '',
    icd10Description: '',
    commonName: '',
    category: '',
    subCategory: '',
    ipcRelevant: true,
    haiType: '',
    isHAI: false,
    riskMultiplier: 1.0,
    nhsnReportable: false,
    cdcNotifiable: false,
    euNotifiable: false,
    euDiseaseCode: '',
    typicalSeverity: 'Moderate',
    causativePathogens: [],
    commonPathogenNames: [],
    affectedSystems: [],
    isActive: true,
    displayOrder: 0
  });

  if (!organizationId) {
    return (
      <div className="diagnosis-manager">
        <div className="error-state">
          <div className="error-icon">⚠</div>
          <h3>{t('organizationNotFound')}</h3>
          <p>{t('createOrganizationFirst')}</p>
        </div>
      </div>
    );
  }

  const handleCreate = () => {
    setEditingDiagnosis(null);
    setFormData({
      icd10Code: '',
      icd10Description: '',
      commonName: '',
      category: '',
      subCategory: '',
      ipcRelevant: true,
      haiType: '',
      isHAI: false,
      riskMultiplier: 1.0,
      nhsnReportable: false,
      cdcNotifiable: false,
      euNotifiable: false,
      euDiseaseCode: '',
      typicalSeverity: 'Moderate',
      causativePathogens: [],
      commonPathogenNames: [],
      affectedSystems: [],
      isActive: true,
      displayOrder: diagnoses.length + 1
    });
    setShowForm(true);
  };

  const handleEdit = (diagnosis) => {
    setEditingDiagnosis(diagnosis);
    setFormData({
      icd10Code: diagnosis.icd10Code || '',
      icd10Description: diagnosis.icd10Description || '',
      commonName: diagnosis.commonName || '',
      category: diagnosis.category || '',
      subCategory: diagnosis.subCategory || '',
      ipcRelevant: diagnosis.ipcRelevant !== undefined ? diagnosis.ipcRelevant : true,
      haiType: diagnosis.haiType || '',
      isHAI: diagnosis.isHAI || false,
      riskMultiplier: diagnosis.riskMultiplier || 1.0,
      nhsnReportable: diagnosis.nhsnReportable || false,
      cdcNotifiable: diagnosis.cdcNotifiable || false,
      euNotifiable: diagnosis.euNotifiable || false,
      euDiseaseCode: diagnosis.euDiseaseCode || '',
      typicalSeverity: diagnosis.typicalSeverity || 'Moderate',
      causativePathogens: diagnosis.causativePathogens || [],
      commonPathogenNames: diagnosis.commonPathogenNames || [],
      affectedSystems: diagnosis.affectedSystems || [],
      isActive: diagnosis.isActive !== undefined ? diagnosis.isActive : true,
      displayOrder: diagnosis.displayOrder || 0
    });
    setShowForm(true);
  };

  const handleDelete = (diagnosis) => {
    setDiagnosisToDelete(diagnosis);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!diagnosisToDelete) return;

    try {
      await deleteDiagnosis(diagnosisToDelete.id);
      notify.success('Diagnosis deleted successfully');
      refresh();
      setShowDeleteConfirm(false);
      setDiagnosisToDelete(null);
    } catch (err) {
      notify.error('Failed to delete diagnosis: ' + err.message);
    }
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.icd10Code) {
        notify.error('ICD-10 code is required');
        return;
      }
      if (!formData.commonName) {
        notify.error('Common name is required');
        return;
      }
      if (!formData.category) {
        notify.error('Category is required');
        return;
      }

      const diagnosisData = {
        ...formData,
        organizationId
      };

      if (editingDiagnosis) {
        await updateDiagnosis(editingDiagnosis.id, diagnosisData, userId, userName);
        notify.success('Diagnosis updated successfully');
      } else {
        await createDiagnosis(diagnosisData, userId, userName);
        notify.success('Diagnosis created successfully');
      }

      refresh();
      setShowForm(false);
      setEditingDiagnosis(null);
    } catch (err) {
      notify.error('Failed to save diagnosis: ' + err.message);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setShowSearchResults(false);
      return;
    }

    await searchDiagnoses(searchTerm);
    setShowSearchResults(true);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const filteredDiagnoses = diagnoses.filter(diagnosis => {
    if (filters.category && diagnosis.category !== filters.category) return false;
    if (filters.haiType && diagnosis.haiType !== filters.haiType) return false;
    if (filters.ipcRelevant && !diagnosis.ipcRelevant) return false;
    if (filters.isHAI && !diagnosis.isHAI) return false;
    return true;
  });

  const categories = [
    'Respiratory Infection',
    'Urinary Tract Infection',
    'Bloodstream Infection',
    'Surgical Site Infection',
    'Gastrointestinal Infection',
    'Skin/Soft Tissue Infection',
    'Central Nervous System Infection',
    'Device-Associated Infection',
    'Other Infection',
    'Non-Infectious Condition'
  ];

  const haiTypes = [
    'CAUTI',
    'CLABSI',
    'VAP',
    'SSI',
    'CDI',
    'Other HAI'
  ];

  const severityLevels = [
    'Mild',
    'Moderate',
    'Severe',
    'Critical'
  ];

  if (loading) {
    return (
      <div className="diagnosis-manager">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('loadingDiagnoses')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="diagnosis-manager">
        <div className="error-state">
          <div className="error-icon">⚠</div>
          <h3>{t('errorLoadingDiagnoses')}</h3>
          <p>{error.message || error}</p>
          <button onClick={refresh} className="btn btn-secondary">{t('retry')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="diagnosis-manager">
      <div className="manager-header">
        <div>
          <h2>{t('diagnosesTitle')}</h2>
          <p className="subtitle">{t('diagnosesSubtitle')}</p>
        </div>
        <div className="header-actions">
          <button
            onClick={() => setShowInitializeModal(true)}
            className="btn btn-secondary"
            disabled={initializing || diagnoses.length > 0}
            title={diagnoses.length > 0 ? t('diagnosesExistReinit') : t('initializeCommonHAI')}
          >
            {initializing ? (
              <>
                <span className="spinner-small"></span>
                {t('initializing')}
              </>
            ) : (
              <>
                🚀 {t('initializeCommonHAI')}
              </>
            )}
          </button>
          <button
            onClick={handleCreate}
            className="btn btn-primary"
            disabled={mutating}
          >
            <Plus size={16} />
            {t('addDiagnosis')}
          </button>
        </div>
      </div>

      {/* Search Bar with WHO API */}
      <div className="search-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder={t('searchDiagnosesPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            className="search-input"
          />
          <button
            onClick={handleSearch}
            disabled={searching || !searchTerm.trim()}
            className="btn btn-secondary"
          >
            <Search size={16} />
            {t('search')}
          </button>
        </div>
        {showSearchResults && (
          <div className="search-results">
            <h4>{t('searchResults')}</h4>
            {searchResults.local.length > 0 && (
              <div>
                <h5>{t('localDiagnoses')} ({searchResults.local.length})</h5>
                <ul>
                  {searchResults.local.map((diagnosis, idx) => (
                    <li key={idx} onClick={() => handleEdit(diagnosis)} className="search-result-item">
                      <strong>{diagnosis.commonName}</strong> ({diagnosis.icd10Code}) - {diagnosis.icd10Description}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {searchResults.whoApi && searchResults.whoApi.length > 0 && (
              <div>
                <h5>{t('whoApi')} ({searchResults.whoApi.length})</h5>
                <p className="search-note">{t('whoApiNote')}</p>
                <ul>
                  {searchResults.whoApi.map((result, idx) => (
                    <li key={idx} className="search-result-item">
                      <strong>{result.icd11Description}</strong> ({result.icd11Code})
                      <button className="btn btn-small" onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          icd11Code: result.icd11Code,
                          icd11Description: result.icd11Description,
                          whoApiData: result.whoApiData
                        }));
                        setShowForm(true);
                      }}>
                        {t('useThis')}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {searchResults.total === 0 && (
              <div className="api-warning">
                <p>⚠️ <strong>{t('noDiagnosesFound')}.</strong> {t('noDiagnosesFoundCreate')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>{t('category')}</label>
          <select
            value={filters.category}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
          >
            <option value="">{t('allCategories')}</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>{t('haiType')}</label>
          <select
            value={filters.haiType}
            onChange={(e) => setFilters(prev => ({ ...prev, haiType: e.target.value }))}
          >
            <option value="">{t('allHAITypes')}</option>
            {haiTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>
            <input
              type="checkbox"
              checked={filters.ipcRelevant}
              onChange={(e) => setFilters(prev => ({ ...prev, ipcRelevant: e.target.checked }))}
            />
            IPC Relevant Only
          </label>
        </div>
        <div className="filter-group">
          <label>
            <input
              type="checkbox"
              checked={filters.isHAI}
              onChange={(e) => setFilters(prev => ({ ...prev, isHAI: e.target.checked }))}
            />
            HAI Only
          </label>
        </div>
      </div>

      {/* Diagnoses List */}
      <div className="diagnoses-list">
        <table className="diagnoses-table">
          <thead>
            <tr>
              <th>ICD-10 Code</th>
              <th>Common Name</th>
              <th>Category</th>
              <th>HAI Type</th>
              <th>Risk Multiplier</th>
              <th>Pathogens</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDiagnoses.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-state">
                  <div className="empty-icon">📋</div>
                  <p>No diagnoses found</p>
                  <button onClick={handleCreate} className="btn btn-primary">
                    <Plus size={16} />
                    Add First Diagnosis
                  </button>
                </td>
              </tr>
            ) : (
              filteredDiagnoses.map(diagnosis => (
                <tr key={diagnosis.id} className={!diagnosis.isActive ? 'inactive-row' : ''}>
                  <td>
                    <span className="code-badge">{diagnosis.icd10Code}</span>
                  </td>
                  <td>
                    <strong>{diagnosis.commonName}</strong>
                    {diagnosis.icd10Description && (
                      <div className="description-text">{diagnosis.icd10Description}</div>
                    )}
                  </td>
                  <td>{diagnosis.category}</td>
                  <td>
                    {diagnosis.haiType ? (
                      <span className="badge badge-hai">{diagnosis.haiType}</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>{diagnosis.riskMultiplier || 1.0}x</td>
                  <td>
                    {diagnosis.commonPathogenNames && diagnosis.commonPathogenNames.length > 0 ? (
                      <div className="pathogen-tags">
                        {diagnosis.commonPathogenNames.slice(0, 2).map((name, idx) => (
                          <span key={idx} className="pathogen-tag">{name}</span>
                        ))}
                        {diagnosis.commonPathogenNames.length > 2 && (
                          <span className="pathogen-tag-more">+{diagnosis.commonPathogenNames.length - 2}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${diagnosis.isActive ? 'active' : 'inactive'}`}>
                      {diagnosis.isActive ? t('active') : t('inactive')}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEdit(diagnosis)}
                        className="btn btn-edit btn-icon"
                        title={t('edit')}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(diagnosis)}
                        className="btn btn-delete btn-icon"
                        title={t('delete')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Form Modal */}
      <Modal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingDiagnosis(null);
        }}
        title={editingDiagnosis ? t('editDiagnosis') : t('createDiagnosis')}
        size="large"
      >
        <div className="diagnosis-form">
          <div className="form-row">
            <div className="form-group">
              <label>{t('icd10Code')} *</label>
              <input
                type="text"
                value={formData.icd10Code}
                onChange={(e) => setFormData(prev => ({ ...prev, icd10Code: e.target.value }))}
                placeholder="e.g., J18.9"
                required
              />
            </div>
            <div className="form-group">
              <label>ICD-10 Description</label>
              <input
                type="text"
                value={formData.icd10Description}
                onChange={(e) => setFormData(prev => ({ ...prev, icd10Description: e.target.value }))}
                placeholder="Official ICD-10 description"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Common Name *</label>
              <input
                type="text"
                value={formData.commonName}
                onChange={(e) => setFormData(prev => ({ ...prev, commonName: e.target.value }))}
                placeholder="e.g., Pneumonia"
                required
              />
            </div>
            <div className="form-group">
              <label>Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                required
              >
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('subCategory')}</label>
              <input
                type="text"
                value={formData.subCategory}
                onChange={(e) => setFormData(prev => ({ ...prev, subCategory: e.target.value }))}
                placeholder={t('placeholderSubCategory')}
              />
            </div>
            <div className="form-group">
              <label>{t('typicalSeverity')}</label>
              <select
                value={formData.typicalSeverity}
                onChange={(e) => setFormData(prev => ({ ...prev, typicalSeverity: e.target.value }))}
              >
                {severityLevels.map(severity => (
                  <option key={severity} value={severity}>{severity}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.ipcRelevant}
                  onChange={(e) => setFormData(prev => ({ ...prev, ipcRelevant: e.target.checked }))}
                />
                {t('ipcRelevant')}
              </label>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isHAI}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, isHAI: e.target.checked }));
                    if (!e.target.checked) {
                      setFormData(prev => ({ ...prev, haiType: '' }));
                    }
                  }}
                />
                {t('healthcareAssociatedInfection')}
              </label>
            </div>
          </div>

          {formData.isHAI && (
            <div className="form-row">
              <div className="form-group">
                <label>{t('haiType')}</label>
                <select
                  value={formData.haiType}
                  onChange={(e) => setFormData(prev => ({ ...prev, haiType: e.target.value }))}
                >
                  <option value="">{t('selectHAIType')}</option>
                  {haiTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('riskMultiplier')}</label>
                <input
                  type="number"
                  min="1.0"
                  max="3.0"
                  step="0.1"
                  value={formData.riskMultiplier}
                  onChange={(e) => setFormData(prev => ({ ...prev, riskMultiplier: parseFloat(e.target.value) }))}
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>{t('riskMultiplier')}</label>
              <input
                type="number"
                min="1.0"
                max="3.0"
                step="0.1"
                value={formData.riskMultiplier}
                onChange={(e) => setFormData(prev => ({ ...prev, riskMultiplier: parseFloat(e.target.value) }))}
              />
              <small>{t('riskMultiplierHint')}</small>
            </div>
            <div className="form-group">
              <label>{t('displayOrder')}</label>
              <input
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: parseInt(e.target.value) }))}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.nhsnReportable}
                  onChange={(e) => setFormData(prev => ({ ...prev, nhsnReportable: e.target.checked }))}
                />
                {t('nhsnReportable')}
              </label>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.cdcNotifiable}
                  onChange={(e) => setFormData(prev => ({ ...prev, cdcNotifiable: e.target.checked }))}
                />
                {t('cdcNotifiable')}
              </label>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.euNotifiable}
                  onChange={(e) => setFormData(prev => ({ ...prev, euNotifiable: e.target.checked }))}
                />
                {t('euNotifiable')}
              </label>
            </div>
          </div>

          {formData.euNotifiable && (
            <div className="form-row">
              <div className="form-group">
                <label>{t('euDiseaseCode')}</label>
                <input
                  type="text"
                  value={formData.euDiseaseCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, euDiseaseCode: e.target.value }))}
                  placeholder={t('placeholderEuCode')}
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                {t('active')}
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              onClick={() => {
                setShowForm(false);
                setEditingDiagnosis(null);
              }}
              className="btn btn-secondary"
              disabled={mutating}
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleSave}
              className="btn btn-primary"
              disabled={mutating}
            >
              <Save size={16} />
              {mutating ? t('initializing') : editingDiagnosis ? t('update') : t('save')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDiagnosisToDelete(null);
        }}
        title={t('deleteDiagnosis')}
        size="small"
      >
        <div className="delete-confirm-content">
          <p>{t('confirmDeleteDiagnosis')}</p>
          {diagnosisToDelete && (
            <div className="delete-details">
              <strong>{diagnosisToDelete.commonName}</strong> ({diagnosisToDelete.icd10Code})
            </div>
          )}
          <p className="warning-text">This action cannot be undone.</p>
          <div className="modal-actions">
            <button
              onClick={() => {
                setShowDeleteConfirm(false);
                setDiagnosisToDelete(null);
              }}
              className="btn btn-secondary"
              disabled={mutating}
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="btn btn-danger"
              disabled={mutating}
            >
              {mutating ? t('initializing') : t('delete')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Initialize Common HAI Diagnoses Modal */}
      <Modal
        isOpen={showInitializeModal}
        onClose={() => {
          setShowInitializeModal(false);
          setInitResult(null);
        }}
        title={t('initializeModalTitle')}
        size="medium"
      >
        <div className="initialize-content">
          <p>{t('initializeModalIntro')}</p>
          <ul className="diagnosis-list">
            <li><strong>CAUTI:</strong> Catheter-Associated Urinary Tract Infection (N39.0)</li>
            <li><strong>CLABSI:</strong> Central Line-Associated Bloodstream Infection (A41.9, T80.211A)</li>
            <li><strong>VAP:</strong> Ventilator-Associated Pneumonia (J18.9, J95.851)</li>
            <li><strong>SSI:</strong> Surgical Site Infection (T81.4XXA)</li>
            <li><strong>CDI:</strong> C. difficile Infection (A04.7)</li>
            <li><strong>Other HAI:</strong> Common healthcare-associated infections (MRSA, MSSA, Candida, etc.)</li>
          </ul>
          <p className="warning-text">⚠️ {t('initializeOnlyIfNoDiagnoses')}</p>
          <div className="modal-actions">
            <button
              onClick={() => {
                setShowInitializeModal(false);
                setInitResult(null);
              }}
              className="btn btn-secondary"
              disabled={initializing}
            >
              {t('cancel')}
            </button>
            <button
              onClick={async () => {
                try {
                  setInitializing(true);
                  setInitResult(null);
                  
                  const result = await initializeCommonHAIDiagnoses(
                    organizationId,
                    userId,
                    userName
                  );
                  
                  setInitResult(result);
                  
                  if (result.success) {
                    notify.success(`Successfully created ${result.created.length} common HAI diagnoses!`);
                    refresh();
                  } else {
                    notify.warning(result.message);
                  }
                } catch (err) {
                  console.error('Error initializing:', err);
                  notify.error('Error initializing diagnoses. Check console for details.');
                  setInitResult({
                    success: false,
                    message: err.message
                  });
                } finally {
                  setInitializing(false);
                }
              }}
              className="btn btn-primary"
              disabled={initializing}
            >
              {initializing ? 'Initializing...' : 'Initialize'}
            </button>
          </div>
          {initResult && (
            <div className={`init-result ${initResult.success ? 'success' : 'error'}`}>
              {initResult.success ? (
                <>
                  <p>✅ {initResult.message}</p>
                  {initResult.created && initResult.created.length > 0 && (
                    <ul>
                      {initResult.created.slice(0, 5).map((item, idx) => (
                        <li key={idx}>{item.commonName} ({item.icd10Code})</li>
                      ))}
                      {initResult.created.length > 5 && (
                        <li>... and {initResult.created.length - 5} more</li>
                      )}
                    </ul>
                  )}
                </>
              ) : (
                <p>❌ {initResult.message}</p>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DiagnosisManager;

